import { useCallback, useEffect, useReducer } from 'react'

import { DateRange, FiltersDefinition, FiltersState } from '@factorialco/f0-react/dist/experimental'

import { useNavConfirmModal } from 'modules/core/hooks/useNavConfirmModal'

import { jsonLogicFiltersToFiltersState } from 'modules/ai_reports/components/DatasourceTypeRenderer/filters'
import { ReportType } from 'modules/ai_reports/lib/types'
import { AiReportsResult } from 'modules/ai_reports/lib/useCreateAnalyticsReportActionResolver'

export type ReportState = {
  report: ReportType | null
  sqlQuery: string
  description: string
  columnFormatHints: AiReportsResult['sqlAnswer']['column_format_hints']
  dateFilter: DateRange | undefined
  datatableFilters: FiltersState<FiltersDefinition<string>> | null
  saveModalOpen: boolean
  hasUnsavedChanges: boolean
  isLoading: boolean
  isSaving: boolean
  error: Error | null
}

export type ReportAction =
  | { type: 'SQL_QUERY_UPDATED'; payload: { sql: string; description: string } }
  | { type: 'DATE_FILTER_UPDATED'; payload: DateRange | undefined }
  | {
      type: 'DATACOLLECTION_FILTERS_UPDATED'
      payload: FiltersState<FiltersDefinition<string>> | null
    }
  | {
      type: 'COLUMN_FORMAT_HINTS_UPDATED'
      payload: AiReportsResult['sqlAnswer']['column_format_hints']
    }
  | { type: 'UNSAVED_CHANGES_CONFIRM_REQUESTED' }
  | { type: 'UNSAVED_CHANGES_DISMISSED' }
  | { type: 'UNSAVED_CHANGES_CONFIRMED' }
  | { type: 'LOADING_CHANGED'; payload: boolean }
  | { type: 'SAVING_CHANGED'; payload: boolean }
  | { type: 'ERROR_CHANGED'; payload: Error | null }
  | { type: 'AGENT_STATE_CHANGED'; payload: Partial<AiReportsResult> }
  | { type: 'REPORT_UPDATED'; payload: Partial<ReportType> }

function reportStateReducer(state: ReportState, action: ReportAction): ReportState {
  switch (action.type) {
    case 'SQL_QUERY_UPDATED':
      return {
        ...state,
        sqlQuery: action.payload.sql,
        description: action.payload.description,
        hasUnsavedChanges: true,
        report:
          state.report && state.report.widgets[0]
            ? {
                ...state.report,
                widgets: [
                  {
                    ...state.report.widgets[0],
                    query: action.payload.sql,
                    description: action.payload.description,
                  },
                ],
              }
            : state.report,
      }

    case 'DATE_FILTER_UPDATED':
      return {
        ...state,
        dateFilter: action.payload,
        hasUnsavedChanges: true,
      }

    case 'DATACOLLECTION_FILTERS_UPDATED':
      return {
        ...state,
        datatableFilters: action.payload,
        hasUnsavedChanges: true,
      }

    case 'COLUMN_FORMAT_HINTS_UPDATED':
      return {
        ...state,
        columnFormatHints: action.payload,
        hasUnsavedChanges: true,
      }

    case 'UNSAVED_CHANGES_CONFIRM_REQUESTED':
      return {
        ...state,
        saveModalOpen: true,
      }

    case 'UNSAVED_CHANGES_DISMISSED':
      return {
        ...state,
        saveModalOpen: false,
      }

    case 'UNSAVED_CHANGES_CONFIRMED':
      return {
        ...state,
        hasUnsavedChanges: false,
      }

    case 'LOADING_CHANGED':
      return {
        ...state,
        isLoading: action.payload,
      }

    case 'SAVING_CHANGED':
      return {
        ...state,
        isSaving: action.payload,
      }

    case 'ERROR_CHANGED':
      return {
        ...state,
        error: action.payload,
      }

    case 'AGENT_STATE_CHANGED': {
      const newState = { ...state }
      const { sqlAnswer, filters } = action.payload

      // Update SQL query and description if changed
      if (sqlAnswer?.sql && sqlAnswer.sql !== state.sqlQuery) {
        newState.sqlQuery = sqlAnswer.sql
        newState.description = sqlAnswer.description || ''
        newState.hasUnsavedChanges = true

        // Update report widgets if report exists
        if (state.report?.widgets?.[0]) {
          newState.report = {
            ...state.report,
            widgets: [
              {
                ...state.report.widgets[0],
                query: sqlAnswer.sql,
                description: sqlAnswer.description || '',
              },
            ],
          }
        }
      }

      if (sqlAnswer?.column_format_hints) {
        newState.columnFormatHints = sqlAnswer.column_format_hints
      }

      if (filters?.jsonLogicFilters) {
        newState.datatableFilters = jsonLogicFiltersToFiltersState(filters.jsonLogicFilters)
      }

      if (filters?.snapshotDate) {
        newState.dateFilter = { from: new Date(filters.snapshotDate) }
      }

      if (filters?.dateRange) {
        newState.dateFilter = {
          from: new Date(filters.dateRange.from),
          to: new Date(filters.dateRange.to),
        }
      }

      return newState
    }

    case 'REPORT_UPDATED':
      return {
        ...state,
        report: state.report ? { ...state.report, ...action.payload } : null,
      }

    default:
      return state
  }
}

export type UseReportCoreStateOptions = {
  datasourceId: string
  initialReport?: ReportType
  initialSqlQuery?: string
  initialDescription?: string
  initialDateFilter?: DateRange
  initialFilters?: ReportType['filters']
  initialColumnFormatHints?: AiReportsResult['sqlAnswer']['column_format_hints']
}

export function useReportCoreState({
  datasourceId,
  initialReport,
  initialSqlQuery,
  initialDescription,
  initialDateFilter,
  initialFilters,
  initialColumnFormatHints = [],
}: UseReportCoreStateOptions) {
  const [state, dispatch] = useReducer(reportStateReducer, {
    report: initialReport || null,
    sqlQuery:
      initialSqlQuery || initialReport?.widgets?.[0]?.query || `SELECT * FROM \`${datasourceId}\``,
    description: initialDescription || initialReport?.widgets?.[0]?.description || '',
    columnFormatHints: initialColumnFormatHints,
    dateFilter: initialDateFilter,
    datatableFilters: initialFilters?.jsonLogicFilters
      ? jsonLogicFiltersToFiltersState(initialFilters.jsonLogicFilters)
      : null,
    saveModalOpen: false,
    hasUnsavedChanges: false,
    isLoading: false,
    isSaving: false,
    error: null,
  })

  const { NavConfirmModal, setNavigationBlocked } = useNavConfirmModal()

  const dispatchMiddleware = useCallback(
    (value: ReportAction) => {
      if (datasourceId.includes('development')) {
        console.log(value)
      }
      dispatch(value)
    },
    [datasourceId]
  )

  // Update navigation blocking when changes are detected
  useEffect(() => {
    if (state.hasUnsavedChanges) {
      setNavigationBlocked(true)
    }
  }, [state.hasUnsavedChanges, setNavigationBlocked])

  return {
    state,
    dispatch: dispatchMiddleware,
    NavConfirmModal,
    setNavigationBlocked,
  }
}
